import { useEffect, useMemo, useRef, useState } from 'react';
import * as PIXI from 'pixi.js';

import { usePixiApp } from '../../services/pixiApp/pixiAppProvider';
import RangeSlider from '../../features/common/containers/RangeSlider';

import './style.css';

const PRESETS = [
    { label: 'Power 0–9000 / 1000', min: 0, max: 9000, step: 1000 },
    { label: 'Cost 0–10 / 1', min: 0, max: 10, step: 1 },
    { label: 'Fine 0–1 / 0.1', min: 0, max: 1, step: 0.1 },
];

const parseConfig = (draft) => {
    const min = Number(draft.min);
    const max = Number(draft.max);
    const step = Number(draft.step);
    const width = Number(draft.width);
    if (![min, max, step, width].every(Number.isFinite)) return null;
    if (!(max > min) || !(step > 0) || width < 160) return null;
    return { min, max, step, width };
};

export default function RangeSliderDemoPage() {
    const { app } = usePixiApp();
    const pixiContainerRef = useRef(null);
    const sliderRef = useRef(null);

    const [draft, setDraft] = useState({ min: '0', max: '9000', step: '1000', width: '720' });
    const [values, setValues] = useState({ low: 0, high: 9000 });
    const [changeCount, setChangeCount] = useState(0);

    const config = useMemo(() => parseConfig(draft), [draft]);

    useEffect(() => {
        if (!app || !pixiContainerRef.current) return undefined;
        if (!pixiContainerRef.current.contains(app.canvas)) {
            pixiContainerRef.current.appendChild(app.canvas);
        }
        return undefined;
    }, [app]);

    useEffect(() => {
        if (!app || !config) return undefined;

        const root = new PIXI.Container();
        const background = new PIXI.Graphics();
        background.rect(0, 0, app.screen.width, app.screen.height);
        background.fill({ color: '#1b2838' });
        root.addChild(background);

        const title = new PIXI.Text({
            text: 'Range slider',
            style: { fontFamily: 'Open Sans', fontSize: 48, fill: '#ffffff', fontWeight: 'bold' },
        });
        title.anchor.set(0.5, 0);
        title.x = app.screen.width / 2;
        title.y = 220;
        root.addChild(title);

        const caption = new PIXI.Text({
            text: '',
            style: { fontFamily: 'Open Sans', fontSize: 40, fill: '#ffffff', fontWeight: 'bold' },
        });
        caption.anchor.set(0.5, 0);
        caption.x = app.screen.width / 2;
        root.addChild(caption);

        const meta = new PIXI.Text({
            text: `min ${config.min}    max ${config.max}    step ${config.step}`,
            style: { fontFamily: 'Open Sans', fontSize: 28, fill: '#c5d2e4' },
        });
        meta.anchor.set(0.5, 0);
        meta.x = app.screen.width / 2;
        root.addChild(meta);

        const slider = new RangeSlider({
            min: config.min,
            max: config.max,
            step: config.step,
            width: config.width,
            onChange: ({ low, high }) => {
                caption.text = `${low} – ${high}`;
                setValues({ low, high });
                setChangeCount((count) => count + 1);
            },
        });
        slider.x = Math.max(0, (app.screen.width - slider.barWidth) / 2);
        slider.y = 420;
        caption.y = slider.y - 80;
        caption.text = `${slider.low} – ${slider.high}`;
        meta.y = slider.y + slider.barHeight + 28;
        root.addChild(slider);

        sliderRef.current = slider;
        app.stage.addChild(root);
        setValues({ low: slider.low, high: slider.high });
        setChangeCount(0);

        return () => {
            sliderRef.current = null;
            app.stage.removeChild(root);
            root.destroy({ children: true });
        };
    }, [app, config]);

    const setField = (field) => (event) => {
        setDraft((current) => ({ ...current, [field]: event.target.value }));
    };

    const applyPreset = (preset) => {
        setDraft((current) => ({
            ...current,
            min: String(preset.min),
            max: String(preset.max),
            step: String(preset.step),
        }));
    };

    const resetValues = () => {
        if (!config || !sliderRef.current) return;
        sliderRef.current.setValues(config.min, config.max);
        setValues({ low: sliderRef.current.low, high: sliderRef.current.high });
    };

    return (
        <div className="range-slider-demo">
            <div className="range-slider-demo__controls">
                <h1>Range slider demo</h1>

                <div className="range-slider-demo__presets">
                    {PRESETS.map((preset) => (
                        <button key={preset.label} type="button" onClick={() => applyPreset(preset)}>
                            {preset.label}
                        </button>
                    ))}
                </div>

                <label>
                    Min
                    <input value={draft.min} onChange={setField('min')} inputMode="decimal" />
                </label>
                <label>
                    Max
                    <input value={draft.max} onChange={setField('max')} inputMode="decimal" />
                </label>
                <label>
                    Step
                    <input value={draft.step} onChange={setField('step')} inputMode="decimal" />
                </label>
                <label>
                    Width
                    <input value={draft.width} onChange={setField('width')} inputMode="numeric" />
                </label>

                <button type="button" onClick={resetValues} disabled={!config}>
                    Reset to full range
                </button>

                {config ? (
                    <p>Selected {values.low} – {values.high}. onChange fired {changeCount} times.</p>
                ) : (
                    <p>Max must be greater than min, step must be above 0, and width at least 160.</p>
                )}
            </div>
            <div className="range-slider-demo__canvas" ref={pixiContainerRef} />
        </div>
    );
}
